import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CircularProgress,
  Button,
  Grid,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import QRCode from "qrcode";
import axios from "src/utils/axios";
import useAuth from "src/hooks/useAuth";
import { useDispatch } from "src/redux/store";
import { requestInstallCallback } from "src/redux/slices/onboarding";
import useOnboardingProgress from "src/hooks/useOnboardingProgress";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import { PATH_APP } from "src/routes/paths";
import { postActionRoute } from "src/utils/postActionRoute";

// ----------------------------------------------------------------------
//   Step final del wizard · Pantalla de BIENVENIDA con código QR para
//   conectar un comandero. Solo llega aquí el user tras completar los
//   steps obligatorios (plan + addons + support). Marca `done` al
//   pulsar "Empezar a vender".
//
//   El QR se genera con `POST /Authentication/GenerateQrAccess` y se
//   pinta como data URL. El comandero lo escanea desde su app para
//   quedar vinculado a la cuenta sin teclear credenciales.
// ----------------------------------------------------------------------

export default function StepFinish({ onComplete }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  const nombreLocal = user?.nombreEmpresa || "tu local";

  // Estado del callback pedido — el backend marca `MeetingScheduledAt`
  // + `MeetingUrl = "callback-requested"` cuando el user pulsa "Que me
  // llamen". Detectamos ambos flags para pintar la card en estado
  // "solicitado" y no permitir doble clic.
  const { progress } = useOnboardingProgress();
  const [callbackRequesting, setCallbackRequesting] = useState(false);
  const callbackAlreadyRequested = !!progress?.meetingScheduledAt;

  const handleRequestCallback = async () => {
    if (callbackAlreadyRequested || callbackRequesting) return;
    setCallbackRequesting(true);
    try {
      await dispatch(requestInstallCallback());
      enqueueSnackbar("Perfecto — un agente te llamará en las próximas horas", { variant: "success" });
    } catch {
      enqueueSnackbar("No hemos podido registrar tu solicitud. Inténtalo de nuevo.", { variant: "error" });
    } finally {
      setCallbackRequesting(false);
    }
  };

  const generateQr = async () => {
    setLoading(true);
    try {
      const res = await axios.post("/Authentication/GenerateQrAccess");
      const token = res?.data;
      if (!token) throw new Error("QR sin token");
      QRCode.toDataURL(
        typeof token === "string" ? token : JSON.stringify(token),
        // 220px es suficiente para escanear con un móvil a 30-40cm
        // sin obligar al user a ampliar el navegador. margin 1 en vez
        // de 2 para dejar más área útil al patrón.
        { width: 220, margin: 1, errorCorrectionLevel: "H" },
        (err, url) => {
          if (err) {
            enqueueSnackbar("No pudimos generar el QR", { variant: "error" });
          } else {
            setQrDataUrl(url);
          }
          setLoading(false);
        },
      );
    } catch {
      enqueueSnackbar("No pudimos generar el QR", { variant: "error" });
      setLoading(false);
    }
  };

  useEffect(() => {
    generateQr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFinish = async () => {
    setSaving(true);
    try {
      // Marcamos el step como completado — el orquestador
      // (OnboardingWizardContent) llamará automáticamente a `done` si
      // es el último step, cerrando el modal.
      await onComplete?.({
        dataJson: JSON.stringify({ qrGenerated: !!qrDataUrl }),
      });
      // Al finalizar el wizard llevamos al user directamente al TPV
      // (plano de mesas o TPV virtual si `isOnlyTpv`). Antes el modal
      // se cerraba y el user quedaba en la ruta donde estaba (típico:
      // dashboard vacío recién registrado) — le tocaba navegar solo.
      history.push(postActionRoute(user));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ textAlign: "center", py: 1 }}>
      {/* Header compacto — chip check + título en una sola fila para
          desktop (stack en móvil), sin el heropanel 80px de antes. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems="center"
        justifyContent="center"
        sx={{ mb: 1.5 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "success.lighter",
            color: "success.dark",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 26 }} />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 900, lineHeight: 1.2 }}
        >
          ¡Todo listo{nombreLocal !== "tu local" ? `, ${nombreLocal}` : ""}!
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mb: 2.5 }}
      >
        Tu TPV ya vende. Conecta un móvil o pídenos que lo hagamos por ti.
      </Typography>

      {/* Grid 2 columnas en desktop, stack en móvil — evita el scroll
          largo de tener las 2 cards apiladas. QR y callback ocupan la
          misma altura visual. */}
      <Grid
        container
        spacing={2}
        sx={{ maxWidth: 720, mx: "auto", alignItems: "stretch" }}
      >
        {/* ── QR de conexión del comandero ── */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Card
            sx={{
              flex: 1,
              p: 2,
              border: "2px dashed",
              borderColor: "primary.light",
              bgcolor: "background.neutral",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{ mb: 1 }}
            >
              <DevicesRoundedIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Escanea con tu móvil
              </Typography>
            </Stack>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                bgcolor: "#fff",
                borderRadius: 1.5,
                p: 1,
              }}
            >
              {loading ? (
                <CircularProgress size={28} />
              ) : qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="Código QR de acceso"
                  style={{
                    display: "block",
                    width: 200,
                    height: 200,
                    maxWidth: "100%",
                  }}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No pudimos generar el código.
                </Typography>
              )}
            </Box>

            <Button
              size="small"
              startIcon={<RefreshRoundedIcon fontSize="small" />}
              onClick={generateQr}
              disabled={loading}
              sx={{ mt: 1, alignSelf: "center", fontSize: 12 }}
            >
              Generar QR nuevo
            </Button>
          </Card>
        </Grid>

        {/* ── Callback de instalación asistida ── */}
        <Grid item xs={12} md={6} sx={{ display: "flex" }}>
          <Card
            sx={{
              flex: 1,
              p: 2,
              border: "1px solid",
              borderColor: callbackAlreadyRequested ? "success.light" : "divider",
              bgcolor: callbackAlreadyRequested
                ? "success.lighter"
                : "background.paper",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {callbackAlreadyRequested ? (
              <>
                <PhoneInTalkRoundedIcon
                  sx={{ fontSize: 32, color: "success.dark", mb: 1, mx: "auto" }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, mb: 0.5, color: "success.darker" }}
                >
                  Te llamaremos en breve
                </Typography>
                <Typography variant="body2" sx={{ color: "success.dark" }}>
                  Un técnico te contactará en las próximas horas para
                  completar la instalación.
                </Typography>
              </>
            ) : (
              <>
                <PhoneInTalkRoundedIcon
                  sx={{ fontSize: 32, color: "primary.main", mb: 1, mx: "auto" }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                  ¿Prefieres que lo hagamos por ti?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mb: 1.5 }}
                >
                  Un técnico te llama gratis y configura TPV, impresoras
                  y primer cobro contigo en directo.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PhoneInTalkRoundedIcon />}
                  disabled={callbackRequesting}
                  onClick={handleRequestCallback}
                  sx={{ alignSelf: "center" }}
                >
                  {callbackRequesting ? "Registrando…" : "Que me llamen"}
                </Button>
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 2, color: "text.disabled", px: 2 }}
      >
        El QR queda accesible desde tu avatar → "Conectar dispositivo".
      </Typography>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleFinish}
          disabled={saving}
          sx={{ minWidth: 260, py: 1.25, fontWeight: 800 }}
        >
          Empezar a vender →
        </Button>
      </Box>
    </Box>
  );
}
